import type { DamageType, Difficulty, StatusKind } from "../types/game";

export const clamp = (value: number, minimum: number, maximum: number): number => Math.max(minimum, Math.min(maximum, value));

export function mitigateDamage(amount: number, type: DamageType, armor: number, magicResist: number): number {
  if (amount <= 0) return 0;
  const resistance = type === "physical" ? armor : type === "magic" ? magicResist : 0;
  return Math.max(1, Math.round(amount * (1 - clamp(resistance, -0.5, 0.8))));
}

export function splashMultiplier(distance: number, radius: number): number {
  if (distance >= radius || radius <= 0) return 0;
  const normalized = distance / radius;
  return 1 - normalized * 0.55;
}

export const difficultyRules: Record<Difficulty, {
  health: number;
  speed: number;
  armor: number;
  bounty: number;
  startingGold: number;
  waveDensity: number;
}> = {
  casual: { health: 0.82, speed: 0.94, armor: -0.04, bounty: 1.08, startingGold: 1.15, waveDensity: 0.88 },
  normal: { health: 1, speed: 1, armor: 0, bounty: 1, startingGold: 1, waveDensity: 1 },
  veteran: { health: 1.2, speed: 1.06, armor: 0.06, bounty: 0.95, startingGold: 0.92, waveDensity: 1.12 },
};

export interface TimedStatus {
  kind: StatusKind;
  remaining: number;
  power: number;
  stacks: number;
}

const stacking: Record<StatusKind, "refresh" | "strongest" | "stack"> = {
  poison: "stack",
  burn: "refresh",
  slow: "strongest",
  stun: "refresh",
  root: "refresh",
  armorBreak: "strongest",
  magicBreak: "strongest",
  mark: "refresh",
};

export function addStatus(statuses: TimedStatus[], incoming: Omit<TimedStatus, "stacks">, immune = false): TimedStatus[] {
  if (immune && incoming.kind !== "mark") return statuses;
  const found = statuses.find((status) => status.kind === incoming.kind);
  if (!found) return [...statuses, { ...incoming, stacks: 1 }];
  const rule = stacking[incoming.kind];
  if (rule === "stack") {
    found.stacks = Math.min(5, found.stacks + 1);
    found.remaining = Math.max(found.remaining, incoming.remaining);
    found.power = Math.max(found.power, incoming.power);
  } else if (rule === "strongest") {
    found.power = Math.max(found.power, incoming.power);
    found.remaining = Math.max(found.remaining, incoming.remaining);
  } else {
    found.remaining = incoming.remaining;
    found.power = incoming.power;
  }
  return statuses;
}

export function updateStatuses(statuses: TimedStatus[], delta: number): TimedStatus[] {
  for (const status of statuses) status.remaining -= delta;
  return statuses.filter((status) => status.remaining > 0);
}

export function starRating(lives: number, startingLives: number): number {
  if (lives <= 0 || startingLives <= 0) return 0;
  const ratio = lives / startingLives;
  return ratio >= 0.75 ? 3 : ratio >= 0.4 ? 2 : 1;
}

export function scoreRun(wave: number, lives: number, gold: number, elapsedSeconds: number, difficulty: Difficulty): number {
  const difficultyMultiplier = difficulty === "casual" ? 0.9 : difficulty === "veteran" ? 1.25 : 1;
  const speedBonus = Math.max(0, 2400 - Math.floor(elapsedSeconds * 3));
  return Math.max(0, Math.round((wave * 1000 + lives * 125 + gold * 2 + speedBonus) * difficultyMultiplier));
}
